import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService, FilterService } from '../../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { MaintenanceCostingService } from '../../../services/maintenance-costing.service';
import { differenceInDays } from 'date-fns';
import { DateInterval } from '../../../../../../_shared/enums';
import { TimeSeriesGroupingHelper } from '../../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { SummaryRequest } from '../../../../../../_shared/models/summary-request.model';
import { hasCompleteDateRange, isSameWidgetFilter } from '../../../../../../_shared/helpers/widget-filter.helper';
import { calculateChartYTicks } from '../../../../../../_shared/helpers/chart-y-ticks.helper';
import { MaintenanceInsightsResponse } from '../../../../maintenance-dashboard/models/maintenance-dashboard-response.model';
import { combineKpiInsights } from '../../../helpers/kpi-insights.helper';
import { EmptyStateComponent } from '../../../../../../_shared/components/states/empty-state/empty-state.component';
import { Subject } from 'rxjs';
import { GraphTrendIconComponent } from '../../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { AccountFilterComponent } from '../../../../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../../../../_shared/components/filters/date-filter/date-filter.component';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../../../../_shared/models/filters/widget-filter-data.model';
import { isEmptyObject } from '../../../../../../_shared/helpers/object.helper';
import { StackedBarChartComponent, StackedBarChartData, StackedBarSubgroup, LegendItem } from '../../../../../../_shared/components/graphs/stacked-bar-chart/stacked-bar-chart.component';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';
import { formatValue } from '../../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-highest-cost-by-contract-type-modal',
  standalone: true,
  imports: [CommonModule, StackedBarChartComponent, EmptyStateComponent, GraphTrendIconComponent, AccountFilterComponent, DateFilterComponent, TranslocoPipe],
  templateUrl: './highest-cost-by-contract-type-modal.component.html',
  styleUrl: './highest-cost-by-contract-type-modal.component.scss'
})
export class HighestCostByContractTypeModalComponent implements OnInit, OnDestroy {
  private activeModal = inject(NgbActiveModal);
  private filterService = inject(FilterService);
  private costingService = inject(MaintenanceCostingService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private authService = inject(AuthService);
  private readonly transloco = inject(TranslocoService);
  private widgetFilter: WidgetFilterData = { ...this.filterService.filter };

  chartData: StackedBarChartData[] = [];
  readonly chartHeight = 335;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;
  readonly subgroups: StackedBarSubgroup[] = [
    { name: 'fmc', legend: 'FMC', color: '#E6B04D' },
    { name: 'fml', legend: 'FML', color: '#BACA62' },
    { name: 'serviceOnly', legend: this.transloco.translate('maintenanceCosting.charts.highestCostByContractType.legend.serviceOnly'), color: '#66AECF' }
  ];

  readonly legendItems: LegendItem[] = [
    { legend: 'FMC', color: '#E6B04D' },
    { legend: 'FML', color: '#BACA62' },
    { legend: this.transloco.translate('maintenanceCosting.charts.highestCostByContractType.legend.serviceOnly'), color: '#66AECF' }
  ];

  isLoading: boolean = true;
  // Set by the opening card to the page's current selection
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];
  totalValue: number = 0;
  percentageChange: number = 0;
  currencySymbol: string = '';
  currencyCode: string = '';

  private cancelSubject: Subject<void> = new Subject<void>();

  get title(): string {
    return this.transloco.translate('maintenanceCosting.charts.highestCostByContractType.title');
  }

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
  }

  async ngOnInit(): Promise<void> {
    await this.loadContractTypeData();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async onAccountFilterChange(filter: AccountFilterData): Promise<void> {
    this.accountFilter = filter;
    this.updateFilter();
  }

  async onDateFilterChange(filter: DateIntervalFilterData): Promise<void> {
    this.dateFilter = filter;
    this.updateFilter();
  }

  async updateFilter(): Promise<void> {
    if (isEmptyObject(this.dateFilter) || isEmptyObject(this.accountFilter)) {
      return;
    }

    const filter = {
      ...this.dateFilter,
      accountIds: [...this.accountFilter.accountIds]
    } as WidgetFilterData;

    // The account filter re-emits the selection the modal opened with once its accounts load
    if (isSameWidgetFilter(filter, this.widgetFilter)) {
      return;
    }

    this.widgetFilter = filter;
    await this.loadContractTypeData();
  }

  async loadContractTypeData(): Promise<void> {
    if (!hasCompleteDateRange(this.widgetFilter)) return;

    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: SummaryRequest = {
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!
      };

      const [fmcResponse, fmlResponse, serviceOnlyResponse] = await Promise.all([
        this.costingService.CostContractByTypeFMC(request, this.cancelSubject),
        this.costingService.CostContractByTypeFML(request, this.cancelSubject),
        this.costingService.CostContractByTypeServiceOnly(request, this.cancelSubject)
      ]);

      this.chartData = this.processContractTypeData(
        fmcResponse.data?.data ?? [],
        fmlResponse.data?.data ?? [],
        serviceOnlyResponse.data?.data ?? []
      );
      this.yTicks = this.calculateYTicks(this.chartData);

      const summary = combineKpiInsights([fmcResponse.data, fmlResponse.data, serviceOnlyResponse.data]);
      this.totalValue = summary.totalValue;
      this.percentageChange = summary.percentageChange;

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.chartData = [];
        }
      });
    }
  }

  private processContractTypeData(
    fmcData: MaintenanceInsightsResponse[],
    fmlData: MaintenanceInsightsResponse[],
    serviceOnlyData: MaintenanceInsightsResponse[]
  ): StackedBarChartData[] {
    if (fmcData.length === 0 && fmlData.length === 0 && serviceOnlyData.length === 0) return [];

    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);

    const datasets = [
      { label: 'fmc', data: fmcData },
      { label: 'fml', data: fmlData },
      { label: 'serviceOnly', data: serviceOnlyData }
    ];

    let groupedData: any[];

    // For 24 hours (1 day): show all 24 individual hours in modal
    if (daysDifference === 0) {
      groupedData = TimeSeriesGroupingHelper.groupByHours(datasets, true);
    }
    // For 1 month (up to 31 days): show every day (no limit for modal)
    else if (daysDifference <= 31) {
      groupedData = TimeSeriesGroupingHelper.groupByDays(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    }
    // For longer periods: group by weeks
    else {
      groupedData = TimeSeriesGroupingHelper.groupByWeeks(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    }

    // Transform grouped data to StackedBarChartData format
    return groupedData.map(item => ({
      label: item.label,
      dateRange: item.dateRange,
      fmc: item.fmc ?? 0,
      fml: item.fml ?? 0,
      serviceOnly: item.serviceOnly ?? 0
    }));
  }

  private calculateYTicks(chartData: StackedBarChartData[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    // For stacked bars, we need to calculate the sum of all segments
    const stackedTotals = chartData.map(item => {
      return (Number(item['fmc']) || 0) +
        (Number(item['fml']) || 0) +
        (Number(item['serviceOnly']) || 0);
    });

    return calculateChartYTicks(stackedTotals, this.defaultYTicks);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return false; // Cost increase is negative
  }

  formatValue(): string {
    return formatValue(this.totalValue, { currencySymbol: this.currencySymbol });
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      return this.subgroups.some(subgroup => {
        const value = Number(item[subgroup.name]) || 0;
        return value > 0;
      });
    });
  }

  close(): void {
    this.activeModal.close();
  }
}